import {
	Icons,
	Container,
	Inline,
	Heading,
	Stack,
	Button,
	Text,
	Table,
	Badge,
	Card,
} from "@sembark-travel/ui-base"
import { TTouristProfile, TTouristProfileDocument } from "./store"
import { XHRLink } from "@sembark-travel/ui-router"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import { FilePreview } from "../Tourists"

export function TouristProfileDocuments({
	touristProfile,
}: {
	touristProfile: TTouristProfile
}) {
	const { id, name_without_salutation, documents } = touristProfile
	return (
		<Container paddingY="8">
			{documents?.length > 0 ? (
				<DocumentSection
					documents={documents}
					id={id}
					name={name_without_salutation}
				/>
			) : (
				<NoDocumentsMessage />
			)}
		</Container>
	)
}

function DocumentSection({
	id,
	name,
	documents,
}: {
	id: number
	name: string
	documents: TTouristProfileDocument[]
}) {
	const hasNoDocuments = documents.every((d) => !d.is_uploaded)
	return (
		<Stack gap="4">
			<Inline justifyContent="between">
				<Heading as="h3">Tourist Profile Documents</Heading>
				<Inline>
					{hasNoDocuments ? (
						<Button
							level="secondary"
							status="primary"
							title={`No documents available for download for ${name}`}
							disabled
						>
							<Icons.DocumentDownload /> Download All Docs
						</Button>
					) : (
						<Button
							level="secondary"
							status="primary"
							as={XHRLink}
							href={`/tourists/profile/${id}/download-zip`}
							download
							target="_blank"
							title={`Download ${name} all documents`}
						>
							<Icons.DocumentDownload /> Download All Docs
						</Button>
					)}
				</Inline>
			</Inline>
			<Table
				bordered
				responsive
				striped
				headers={[
					"S No.",
					"Document Type",
					"Document Id",
					"Document Status",
					"",
				]}
				alignCols={{ 4: "right" }}
				rows={documents?.map(
					(
						{ name, uid, media_url: mediaUrl, document_id: documentId },
						index
					) => [
						<Text>{index + 1}</Text>,
						<Text>{name}</Text>,
						<Text>{uid}</Text>,
						<Badge success={Boolean(mediaUrl)} warning={!mediaUrl}>
							{mediaUrl ? "Uploaded" : "Pending"}
						</Badge>,
						mediaUrl ? (
							<Inline gap="4" justifyContent="end">
								<DocumentPreviewDialog url={mediaUrl}>
									{({ showPreview }) => (
										<Button onClick={() => showPreview()}>
											<Icons.ArrowExpand /> Preview
										</Button>
									)}
								</DocumentPreviewDialog>
								<Button
									level="secondary"
									status="primary"
									as={XHRLink}
									href={`/tourist-profiles/${id}/documents/${documentId}/download`}
									download
									target="_blank"
									title={`Download ${name}`}
								>
									<Icons.DocumentDownload /> Download
								</Button>
							</Inline>
						) : null,
					]
				)}
			/>
		</Stack>
	)
}

export function DocumentPreviewDialog({
	url,
	children,
}: {
	url?: string
	children: (props: { showPreview: () => void }) => React.ReactNode
}) {
	const [isOpen, open, close] = useDialog()
	return (
		<>
			{children({ showPreview: open })}
			<Dialog open={isOpen} onClose={close} title={url?.split("/").at(-1)} sm>
				<Dialog.Body>
					<Stack gap="2">
						<Card bgColor="subtle" padding="4">
							<FilePreview url={url} />
						</Card>
					</Stack>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function NoDocumentsMessage() {
	return (
		<Stack gap="2" alignItems="center">
			<Icons.Info size="10" color="muted" />
			<Heading as="h3" fontSize="lg" fontWeight="normal">
				No Documents Uploaded
			</Heading>
			<Text color="muted">
				This tourist profile does not have any documents yet.
			</Text>
		</Stack>
	)
}
